
// Libraries
import { defineComponent } from 'vue';
import { mapStores } from 'pinia';

// Components
import Checkbox from 'client/components/forms/Checkbox.vue';
import LoginActionButton from 'client/components/LoginActionButton.vue';
import LoginAlert from 'client/components/LoginAlert.vue';
import LoginTextField from 'client/components/LoginTextField.vue';
import UserLoginStore from 'client/stores/UserLoginStore';

// Services
import LocalStorageService from 'client/util/LocalStorageService';
import userLoginService from 'client/services/userLoginService';

// Constants
import { LocalStorageConsts, LoginConsts } from 'client/util/Constants';
import { useLoginLabels } from 'client/util/Constants/LoginLabels';
import RequestError from 'client/services/response/RequestError';

// Utils
import { datadogRum } from '@datadog/browser-rum-slim';

const {
  userLoginEmail,
} = LocalStorageConsts;
const {
  responseMessages,
} = LoginConsts;
const {
  passwordRequired,
} = responseMessages;

export default defineComponent({
  name: 'EmailEntry',

  inject: ['$validator'],

  components: {
    Checkbox,
    LoginActionButton,
    LoginAlert,
    LoginTextField,
  },

  props: {
    successMessage: {
      type: String,
      default: '',
    },
    isSecondarySubmitButton: {
      type: Boolean,
      default: false,
    },
  },

  setup() {
    const uiLabel = useLoginLabels();
    return { uiLabel };
  },

  mounted() {
    this.userLoginStore.password = '';
  },

  data() {
    return {
      hasTracked: false,
      isLoading: false,
    };
  },

  computed: {
    ...mapStores(UserLoginStore),

    disableButton() {
      const errors = this.$validator.errors.items.length > 0;

      return errors || !this.userLoginStore.email.length;
    },
  },

  methods: {
    handleError() {
      this.$emit('error', new RequestError(0, passwordRequired));
    },

    handleFocus() {
      if (!this.hasTracked) {
        this.hasTracked = true;
        // Identify JS errors that cause the page not to load for specific browsers
        datadogRum.addAction('user-ready');
      }
    },

    handleInput(isChecked: boolean) {
      if (!isChecked) {
        LocalStorageService.removeItem(userLoginEmail);
      }
    },

    handleSuccess() {
      this.resetErrorMessage();
      this.$emit('handleComponentChange', 'password');
    },

    resetErrorMessage() {
      this.$emit('resetErrorMessage');
    },

    async submit() {
      this.resetErrorMessage();
      this.isLoading = true;

      try {
        await userLoginService.submitEmail(this.userLoginStore.email);
        this.handleSuccess();
      } catch {
        this.handleError();
      } finally {
        this.isLoading = false;
      }
    },
  },
});
